import React from "react";

export interface ParsedEntityLink {
    entityType: string;
    entityId: string;
    displayText: string;
}

export interface KnownEntityRef {
    entityType: string;
    entityId: string;
}

// Why this parser is more permissive than the token format the backend emits:
//
// The backend emits a strict 〔e:Type:GUID:DisplayText〕 token, but the assistant message that
// reaches us is the *model's rendering* of that token, not the raw backend output. Two real
// failure modes have been observed in production with mistral-medium-3.5:
//   1. Bracket substitution — the model swaps 〔〕 (U+3014/U+3015) for 【】 (U+3010/U+3011),
//      because the same model is also instructed to emit citations with 【】 and bleed-through
//      happens. We accept either pair and tolerate mismatched pairs.
//   2. GUID dash-dropping — the model occasionally rewrites the GUID without the canonical
//      8-4-4-4-12 dash separators, e.g. `03686374-3e68-4c296e4b0f17` instead of the full
//      `03686374-3e68-XXXX-XXXX-4c296e4b0f17`. Without the relaxed regex these tokens slip
//      through as plain text and the user sees an unclickable artefact in the assistant's reply.
//
// We can't fix the model's output deterministically (it's a sampling artefact), and tightening
// the prompt only catches the obvious cases. So we accept a loose 20–40-char id segment and
// reconcile it against the canonical ids the backend already streamed for this turn (entity
// references + citation sources). That gives us ground-truth recovery without false positives:
// if no known id prefix-matches the malformed capture we leave the raw token in place rather
// than guess.
const entityLinkRegex = /[〔【]e:([A-Za-z]+):([0-9a-fA-F-]{20,40}):([^〕】]+)[〕】]/g;
const canonicalGuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

function stripDashes(id: string): string {
    return id.replace(/-/g, "").toLowerCase();
}

// Number of leading hex chars (= GUID segment 1) we trust the model to preserve verbatim. Even
// when Medium 3.5 mangles a GUID it almost always keeps the first 8 chars intact — a real case
// observed in production: real `03686374-3e68-461c-89bc-40296e4b0f17` came back as
// `03686374-3e68-4c296e4b0f17`, with a middle 8-char chunk dropped *and* a 1-char mutation in
// the tail. The first 8 hex chars survived both edits. 8 hex = 32 bits, which is selective
// enough that two known entities in the same turn colliding on this prefix is vanishingly rare.
const TrustedHexPrefixLength = 8;

// Try to resolve a malformed entity id against the ground-truth ids emitted by the backend
// during this turn. Strategy, in order of confidence:
//   1. Canonical GUID — return as-is.
//   2. Strict prefix-match — one side's stripped-hex form is a prefix of the other's. Catches
//      pure dash-dropping or trailing truncation with no mutations.
//   3. Trusted-prefix-match — match on the first N hex chars only. Catches the harder case where
//      the model dropped a middle chunk and/or mutated a character.
// In every case the candidate must share the same entityType and there must be exactly one
// surviving match, otherwise we return null and let the caller render the raw token.
function resolveEntityId(capturedId: string, entityType: string, known: KnownEntityRef[]): string | null {
    if (canonicalGuidRegex.test(capturedId)) {
        return capturedId;
    }
    if (known.length === 0) {
        return null;
    }

    const capturedHex = stripDashes(capturedId);
    if (capturedHex.length === 0) {
        return null;
    }

    const sameTypeKnown = known.filter((k) => k.entityType === entityType);

    const strictMatches = sameTypeKnown.filter((k) => {
        const knownHex = stripDashes(k.entityId);
        return knownHex.startsWith(capturedHex) || capturedHex.startsWith(knownHex);
    });
    if (strictMatches.length === 1) {
        return strictMatches[0].entityId;
    }

    if (capturedHex.length >= TrustedHexPrefixLength) {
        const capturedPrefix = capturedHex.slice(0, TrustedHexPrefixLength);
        const prefixMatches = sameTypeKnown.filter((k) => stripDashes(k.entityId).startsWith(capturedPrefix));
        if (prefixMatches.length === 1) {
            return prefixMatches[0].entityId;
        }
    }

    return null;
}

// Characters that already provide a natural visual gap before a link, so no synthetic space is
// needed. Includes whitespace, opening brackets/parens, and punctuation that ends with a quote
// or paragraph marker. Anything else (a letter, digit, period, comma, …) gets a leading space
// injected when the model placed the entity token flush against the prior text.
function needsLeadingSpaceBeforeLink(precedingChars: string): boolean {
    if (precedingChars.length === 0) return false;
    const last = precedingChars[precedingChars.length - 1];
    return !/[\s([{<"'“‘]/.test(last);
}

export function splitEntityLinks(
    text: string,
    renderLink: (link: ParsedEntityLink, key: number) => React.ReactNode,
    knownEntities: KnownEntityRef[] = []
): React.ReactNode[] {
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    let tokenIndex = 0;

    let match;
    entityLinkRegex.lastIndex = 0;
    while ((match = entityLinkRegex.exec(text)) !== null) {
        let precedingText: string | null = null;
        if (match.index > lastIndex) {
            precedingText = text.slice(lastIndex, match.index);
        }

        const entityType = match[1];
        const resolvedId = resolveEntityId(match[2], entityType, knownEntities);
        if (resolvedId === null) {
            // Couldn't reconcile a malformed id against the turn's known entities — preserve the
            // raw token so the user sees the model's text instead of silently dropping it.
            if (precedingText !== null) result.push(precedingText);
            result.push(match[0]);
            lastIndex = match.index + match[0].length;
            continue;
        }

        // The LLM occasionally emits the entity token flush against the prior word (e.g.
        // `...GDPR.〔e:Contract:…〕`). Without a separator the link's display text bumps right
        // up against the previous character — "GDPR.7 - std" — which reads as a typo. Inject a
        // single space when the preceding character is something other than whitespace or an
        // opening bracket/quote.
        if (precedingText !== null) {
            result.push(needsLeadingSpaceBeforeLink(precedingText) ? precedingText + " " : precedingText);
        }

        const link: ParsedEntityLink = {
            entityType,
            entityId: resolvedId,
            displayText: match[3],
        };

        const rendered = renderLink(link, tokenIndex++);
        result.push(rendered);

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        result.push(text.slice(lastIndex));
    }

    return result;
}

export function processEntityLinks(
    children: React.ReactNode,
    renderLink: (link: ParsedEntityLink, key: number) => React.ReactNode,
    knownEntities: KnownEntityRef[] = []
): React.ReactNode {
    return React.Children.map(children, (child) => {
        if (typeof child === "string") {
            const parts = splitEntityLinks(child, renderLink, knownEntities);
            return parts.length === 1 ? parts[0] : React.createElement(React.Fragment, null, ...parts);
        }
        if (React.isValidElement(child) && child.props.children) {
            return React.cloneElement(child, {}, processEntityLinks(child.props.children, renderLink, knownEntities));
        }
        return child;
    });
}
